import React, { useEffect, useMemo, useState } from 'react';

const emptyForm = {
  room_type_id: '',
  room_numbers_text: '',
  floor_no: '',
  block_name: ''
};

function PmsRoomInventorySetup({ token, selectedProperty }) {
  const [types, setTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadData = async () => {
    if (!selectedProperty?.id) {
      setTypes([]);
      setRooms([]);
      return;
    }
    try {
      const [typesRes, roomsRes] = await Promise.all([
        fetch('/api/v1/pms/room-types?all=1', { headers: headers() }),
        fetch('/api/v1/pms/rooms?all=1', { headers: headers() })
      ]);
      const tData = await typesRes.json();
      const rData = await roomsRes.json();
      if (!typesRes.ok) throw new Error(tData?.error || 'Failed to load room types');
      if (!roomsRes.ok) throw new Error(rData?.error || 'Failed to load rooms');
      setTypes(Array.isArray(tData) ? tData : []);
      setRooms(Array.isArray(rData) ? rData : []);
    } catch (err) {
      setError(err?.message || 'Failed to load inventory setup data');
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const selectedType = useMemo(
    () => types.find((t) => String(t.room_type_id) === String(form.room_type_id)) || null,
    [types, form.room_type_id]
  );

  const activeRoomsForType = useMemo(
    () => rooms.filter((r) => String(r.room_type_id) === String(form.room_type_id) && r.is_active !== false),
    [rooms, form.room_type_id]
  );

  const draftRoomNumbers = useMemo(() => {
    const raw = String(form.room_numbers_text || '')
      .split(/[\n,]+/g)
      .map((v) => String(v || '').trim().toUpperCase())
      .filter(Boolean);
    return Array.from(new Set(raw));
  }, [form.room_numbers_text]);

  const existingRoomMap = useMemo(() => {
    const m = new Map();
    rooms.forEach((r) => m.set(String(r.room_number || '').trim().toUpperCase(), true));
    return m;
  }, [rooms]);

  const duplicateWarnings = useMemo(
    () => draftRoomNumbers.filter((n) => existingRoomMap.has(n)),
    [draftRoomNumbers, existingRoomMap]
  );

  const targetRooms = Number(selectedType?.total_rooms || 0);
  const createdCount = activeRoomsForType.length;
  const pendingCount = Math.max(0, targetRooms - createdCount);

  const createRooms = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!form.room_type_id) return setError('Select room type');
    if (!draftRoomNumbers.length) return setError('Enter at least one room number');
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/pms/rooms/bulk-by-type', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          room_type_id: Number(form.room_type_id),
          room_numbers: draftRoomNumbers,
          floor_no: form.floor_no,
          block_name: form.block_name
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to create rooms');
      setMessage(`${Number(data.created_count || 0)} room(s) created successfully`);
      setForm((prev) => ({ ...prev, room_numbers_text: '' }));
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to create rooms');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Room Config</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#334155' }}>
        Select a room type, enter room numbers, and create all rooms in one action.
      </div>

      <div className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.6rem' }}>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Type *</span>
          <select value={form.room_type_id} onChange={(e) => setForm({ ...form, room_type_id: e.target.value })}>
            <option value="">Select Room Type</option>
            {types
              .filter((t) => t.is_active !== false)
              .map((t) => (
                <option key={t.room_type_id} value={t.room_type_id}>
                  {t.room_type_name} ({t.room_type_code})
                </option>
              ))}
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Default Floor (optional)</span>
          <input
            placeholder="Ex: 1 / 2 / Ground"
            value={form.floor_no}
            onChange={(e) => setForm({ ...form, floor_no: e.target.value })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Default Block (optional)</span>
          <input
            placeholder="Ex: A / B / East Wing"
            value={form.block_name}
            onChange={(e) => setForm({ ...form, block_name: e.target.value })}
          />
        </label>

        <div style={{ display: 'grid', gap: '0.35rem', alignContent: 'end' }}>
          <div style={{ fontSize: '0.82rem', color: '#0f172a' }}>Target: <b>{targetRooms}</b></div>
          <div style={{ fontSize: '0.82rem', color: '#0f172a' }}>Created: <b>{createdCount}</b></div>
          <div style={{ fontSize: '0.82rem', color: '#0f172a' }}>Pending: <b>{pendingCount}</b></div>
        </div>
      </div>

      <div style={{ marginTop: '0.7rem', display: 'grid', gap: '0.35rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Numbers * (comma or new line)</span>
        {form.room_type_id && activeRoomsForType.length > 0 && (
          <div style={{ fontSize: '0.78rem', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '0.4rem 0.6rem' }}>
            <b>Existing rooms ({activeRoomsForType.length}):</b>{' '}
            {activeRoomsForType.map(r => r.room_number).sort().join(', ')}
          </div>
        )}
        <textarea
          rows={5}
          placeholder={form.room_type_id && activeRoomsForType.length > 0
            ? 'Enter new room numbers (existing: ' + activeRoomsForType.map(r => r.room_number).sort().join(', ') + ')'
            : 'Example:\n101, 102, 103\nor\nA101\nA102'}
          value={form.room_numbers_text}
          onChange={(e) => setForm({ ...form, room_numbers_text: e.target.value })}
          style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.55rem' }}
        />
        <div style={{ fontSize: '0.8rem', color: '#475569' }}>Entered unique rooms: <b>{draftRoomNumbers.length}</b></div>
        {duplicateWarnings.length ? (
          <div style={{ fontSize: '0.8rem', color: '#b91c1c' }}>
            Already existing rooms: {duplicateWarnings.join(', ')}
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
        <button onClick={createRooms} disabled={loading}>{loading ? 'Creating...' : 'Create Rooms'}</button>
        <button onClick={() => setForm((prev) => ({ ...prev, room_numbers_text: '' }))}>Clear Numbers</button>
      </div>
    </div>
  );
}

export default PmsRoomInventorySetup;
